/**
 * The sample workforce for the HR suite.
 *
 * A maintenance-and-operations workforce of the size an airport department
 * carries — airfield, terminal engineering, systems, fire and life safety,
 * airside operations, administration — with Saudi and expatriate staff on
 * realistic pay, some hired under the 2024 GOSI schedule, two Saudis under the
 * Nitaqat full-count wage, one leaver, and outsourced crews on contract
 * charges (one with no charge entered, so the coverage line has something to
 * say). These are the demonstration's people, not Madinah's staff, and every
 * screen says so.
 *
 * Every figure is computed from these records with the same arithmetic the
 * platform uses (`domain/hr.ts`); the work it prices is the sample register's
 * own (`mock/assetManagement.ts`), so a repair on a chiller costs what the
 * HVAC technicians cost.
 */
import {
  costLine,
  costSummary,
  describeEmployee,
  labour,
  mergeRules,
  saudization,
  tradeOfCategory,
  TRADES,
  type HrCost,
  type HrEmployee,
  type HrEmployeeInput,
  type HrEmployeePatch,
  type HrLabour,
  type HrOvertimeEntry,
  type HrPeople,
  type HrPlanRecord,
  type HrRulesPatch,
  type HrRulesView,
  type HrSaudization,
  type HrUser,
  type HrWorkRecord,
  type Trade,
  defaultRules,
} from "../domain/hr";
import type { MockAssetManagement } from "./assetManagement";
import type { HrAccessView } from "../domain/hrAccess";
import type { ImportResponse, ImportResult } from "../domain/hrImport";
import {
  dayStatus,
  locate,
  REQUEST_BY,
  type ClockMethod,
  type HrChannels,
  type HrClockEvent,
  type HrClockInput,
  type HrGeofence,
  type HrMe,
  type HrNotification,
  type HrSelfRequest,
  type NotifyEvent,
} from "../domain/hrClock";
import {
  type AttendanceStatus,
  type HrAttendanceDay,
  type HrAttendanceInput,
  type HrAttendanceRow,
  type HrRecord,
  type HrRecordInput,
  type HrStaffFile,
  type RecordStatus,
} from "../domain/hrFile";
import { TODAY } from "./clock";

const DAY = 86_400_000;
const iso = (d: Date) => d.toISOString().slice(0, 10);

function seq(seed: number) {
  let x = seed;
  return () => {
    x = (x * 1103515245 + 12345) % 2147483648;
    return x / 2147483648;
  };
}

const SAUDI_FIRST = [
  "Abdullah",
  "Faisal",
  "Saad",
  "Khalid",
  "Omar",
  "Turki",
  "Majed",
  "Nawaf",
  "Sultan",
  "Yousef",
  "Hamad",
  "Bandar",
  "Fahad",
  "Ibrahim",
  "Ahmed",
  "Mohammed",
  "Abdulrahman",
  "Ziyad",
  "Rakan",
  "Hussain",
  "Noura",
  "Reem",
  "Sara",
  "Hessa",
  "Lama",
  "Amal",
];
const SAUDI_FAMILY = [
  "Al-Harbi",
  "Al-Juhani",
  "Al-Mutairi",
  "Al-Otaibi",
  "Al-Qahtani",
  "Al-Ghamdi",
  "Al-Zahrani",
  "Al-Shehri",
  "Al-Anazi",
  "Al-Subaie",
  "Al-Rashidi",
  "Al-Dosari",
  "Al-Sahli",
  "Al-Maliki",
];
const EXPAT_NAMES: Record<string, string[]> = {
  IN: ["Rajesh Kumar", "Suresh Nair", "Anil Menon", "Vijay Pillai", "Manoj Das", "Sanjay Rao", "Prakash Iyer"],
  PK: ["Imran Khan", "Asif Mahmood", "Tariq Aziz", "Naveed Iqbal", "Kashif Ali", "Bilal Ahmed"],
  PH: ["Jose Santos", "Mark Reyes", "Ramon Cruz", "Arnel Bautista", "Dennis Garcia"],
  EG: ["Mahmoud Hassan", "Ahmed Fathy", "Mostafa Salem", "Tamer Adel", "Hany Mansour"],
  JO: ["Khaled Haddad", "Rami Nasser", "Omar Qasem"],
  BD: ["Rafiqul Islam", "Kamal Hossain", "Shahidul Alam", "Mizanur Rahman"],
  NP: ["Ram Bahadur", "Bikash Thapa", "Suman Gurung"],
  SD: ["Osman Idris", "Hassan Babiker"],
};

interface Spec {
  department: string;
  costCentre: string;
  trade: Trade;
  title: string;
  n: number;
  saudiShare: number;
  saudiBasic: [number, number];
  expatBasic: [number, number];
  nats: string[];
  grade: string;
  bind: number;
}

const SPECS: Spec[] = [
  {
    department: "Airfield Maintenance",
    costCentre: "CC-AFM",
    trade: "electrical",
    title: "AGL technician",
    n: 8,
    saudiShare: 0.4,
    saudiBasic: [6000, 8000],
    expatBasic: [2600, 3800],
    nats: ["IN", "PK", "PH"],
    grade: "T2",
    bind: 0.75,
  },
  {
    department: "Airfield Maintenance",
    costCentre: "CC-AFM",
    trade: "electrical",
    title: "Electrical engineer",
    n: 2,
    saudiShare: 0.5,
    saudiBasic: [13000, 17000],
    expatBasic: [9500, 12500],
    nats: ["EG", "JO"],
    grade: "E2",
    bind: 0.5,
  },
  {
    department: "Airfield Maintenance",
    costCentre: "CC-AFM",
    trade: "civil",
    title: "Civil technician",
    n: 5,
    saudiShare: 0.2,
    saudiBasic: [5800, 7200],
    expatBasic: [2200, 3200],
    nats: ["PK", "BD", "IN"],
    grade: "T1",
    bind: 0.6,
  },
  {
    department: "Airfield Maintenance",
    costCentre: "CC-AFM",
    trade: "civil",
    title: "Pavement engineer",
    n: 1,
    saudiShare: 1,
    saudiBasic: [14000, 16000],
    expatBasic: [0, 0],
    nats: [],
    grade: "E2",
    bind: 0,
  },
  {
    department: "Terminal Engineering",
    costCentre: "CC-TEN",
    trade: "mechanical",
    title: "HVAC technician",
    n: 6,
    saudiShare: 0.34,
    saudiBasic: [6200, 8000],
    expatBasic: [2800, 4000],
    nats: ["IN", "PH", "EG"],
    grade: "T2",
    bind: 0.7,
  },
  {
    department: "Terminal Engineering",
    costCentre: "CC-TEN",
    trade: "conveyance",
    title: "BHS technician",
    n: 4,
    saudiShare: 0.5,
    saudiBasic: [6500, 8500],
    expatBasic: [3000, 4200],
    nats: ["PH", "IN"],
    grade: "T2",
    bind: 0.5,
  },
  {
    department: "Terminal Engineering",
    costCentre: "CC-TEN",
    trade: "electrical",
    title: "Power technician",
    n: 3,
    saudiShare: 0.34,
    saudiBasic: [6500, 8200],
    expatBasic: [2800, 3800],
    nats: ["IN", "PK"],
    grade: "T2",
    bind: 0.7,
  },
  {
    department: "Terminal Engineering",
    costCentre: "CC-TEN",
    trade: "mechanical",
    title: "Mechanical engineer",
    n: 2,
    saudiShare: 0.5,
    saudiBasic: [13000, 16500],
    expatBasic: [10000, 13000],
    nats: ["EG", "JO"],
    grade: "E2",
    bind: 0.5,
  },
  {
    department: "Systems & ICT",
    costCentre: "CC-ICT",
    trade: "systems",
    title: "Systems technician",
    n: 4,
    saudiShare: 0.75,
    saudiBasic: [7000, 9500],
    expatBasic: [4500, 6500],
    nats: ["IN", "JO"],
    grade: "T3",
    bind: 0.75,
  },
  {
    department: "Fire & Life Safety",
    costCentre: "CC-FLS",
    trade: "safety",
    title: "Fire systems technician",
    n: 3,
    saudiShare: 0.67,
    saudiBasic: [6500, 8500],
    expatBasic: [3200, 4200],
    nats: ["EG"],
    grade: "T2",
    bind: 0.67,
  },
  {
    department: "Airside Operations",
    costCentre: "CC-OPS",
    trade: "operations",
    title: "Airside operations officer",
    n: 8,
    saudiShare: 0.88,
    saudiBasic: [7500, 10500],
    expatBasic: [5000, 6000],
    nats: ["SD"],
    grade: "O2",
    bind: 0,
  },
  {
    department: "Administration",
    costCentre: "CC-ADM",
    trade: "administration",
    title: "HR, finance and procurement officer",
    n: 5,
    saudiShare: 1,
    saudiBasic: [7000, 14000],
    expatBasic: [0, 0],
    nats: [],
    grade: "A2",
    bind: 0,
  },
  {
    department: "Management",
    costCentre: "CC-MGT",
    trade: "administration",
    title: "Manager",
    n: 3,
    saudiShare: 1,
    saudiBasic: [24000, 32000],
    expatBasic: [0, 0],
    nats: [],
    grade: "M1",
    bind: 0,
  },
];

const OUTSOURCED: {
  department: string;
  costCentre: string;
  trade: Trade;
  title: string;
  n: number;
  rate: [number, number];
  nats: string[];
}[] = [
  {
    department: "Terminal Engineering",
    costCentre: "CC-TEN",
    trade: "mechanical",
    title: "FM contractor technician",
    n: 6,
    rate: [5200, 6800],
    nats: ["IN", "PK", "BD", "NP"],
  },
  {
    department: "Airfield Maintenance",
    costCentre: "CC-AFM",
    trade: "civil",
    title: "Pavement crew (contract)",
    n: 5,
    rate: [3200, 3800],
    nats: ["BD", "NP", "PK"],
  },
];

type Stored = Omit<HrEmployee, "saudi" | "serviceYears" | "gosiRegime" | "gosiRegimeSource">;

export class MockHr {
  private employees: Stored[] = [];
  private users: HrUser[] = [];
  private overtime: HrOvertimeEntry[] = [];
  private restated: HrRulesPatch = {
    nitaqat: { targetPct: 35 },
    /* the airports pack's own categories, mapped by the airport */
    tradeByCategory: {
      ...defaultRules().tradeByCategory,
      "airfield lighting": "electrical",
      "terminal plant": "mechanical",
      landside: "civil",
      screening: "systems",
    },
  };
  private rulesAt: string = new Date(TODAY.getTime() - 40 * DAY).toISOString();
  private seqNo = 1000;
  private records: HrRecord[] = [];
  private attendance = new Map<string, HrAttendanceRow>();
  private photos = new Map<string, string>();
  private access = new Map<string, string>();
  private recSeq = 0;
  private letterSeq = 0;
  private fences: HrGeofence[] = [];
  private clockEvents: HrClockEvent[] = [];
  private outbox: HrNotification[] = [];
  private notifySeq = 0;
  private meId: string | null = null;

  private readonly am: MockAssetManagement;

  constructor(am: MockAssetManagement) {
    this.am = am;
    const rnd = seq(20260927);
    const pick = <T>(xs: T[]) => xs[Math.floor(rnd() * xs.length)];
    const between = ([a, b]: [number, number]) => Math.round((a + rnd() * (b - a)) / 50) * 50;
    const usedNames = new Set<string>();
    const saudiName = () => {
      for (let i = 0; i < 50; i++) {
        const n = `${pick(SAUDI_FIRST)} ${pick(SAUDI_FAMILY)}`;
        if (!usedNames.has(n)) {
          usedNames.add(n);
          return n;
        }
      }
      return `${pick(SAUDI_FIRST)} ${pick(SAUDI_FAMILY)} ${usedNames.size}`;
    };
    const expatName = (nat: string) => {
      const pool = EXPAT_NAMES[nat] ?? ["Staff member"];
      for (const n of pool)
        if (!usedNames.has(n)) {
          usedNames.add(n);
          return n;
        }
      const n = `${pick(pool)} ${usedNames.size}`;
      usedNames.add(n);
      return n;
    };
    const hired = () => iso(new Date(TODAY.getTime() - Math.round((0.4 + rnd() * 13.5) * 365.25) * DAY));

    for (const sp of SPECS) {
      const saudis = Math.round(sp.n * sp.saudiShare);
      for (let i = 0; i < sp.n; i++) {
        const saudi = i < saudis;
        const nat = saudi ? "SA" : pick(sp.nats);
        const basic = between(saudi ? sp.saudiBasic : sp.expatBasic);
        const bound = rnd() < sp.bind;
        const id = `hr-emp-${++this.seqNo}`;
        const name = saudi ? saudiName() : expatName(nat);
        let principalId: string | null = null;
        if (bound) {
          principalId = `hr-user-${this.seqNo}`;
          this.users.push({ id: principalId, displayName: name, email: null });
        }
        this.employees.push({
          id,
          siteId: null,
          employeeNo: `E${this.seqNo}`,
          name,
          nationality: nat,
          department: sp.department,
          trade: sp.trade,
          jobTitle: sp.title,
          grade: sp.grade,
          employment: "direct",
          contractType: saudi ? "indefinite" : "fixed",
          hiredOn: hired(),
          contractEnd: saudi ? null : iso(new Date(TODAY.getTime() + Math.round(60 + rnd() * 600) * DAY)),
          leftOn: null,
          status: rnd() < 0.05 ? "on_leave" : "active",
          basic,
          housing: Math.round(basic * 0.25),
          transport: basic >= 20000 ? 2000 : saudi ? Math.round(basic * 0.1) : 400,
          other: sp.grade === "M1" ? 3000 : 0,
          outsourcedMonthly: null,
          medicalAnnual: sp.grade === "M1" ? 12000 : null,
          gosiNewEntrant: null,
          weeklyHours: 48,
          costCentre: sp.costCentre,
          principalId,
          principalName: principalId ? name : null,
          notes: null,
        });
      }
    }
    // two Saudis under the Nitaqat full-count wage: one counts half, one not at all
    const low: [string, string, Trade, string, number, number][] = [
      ["Administration", "CC-ADM", "administration", "Office assistant", 2800, 600],
      ["Airside Operations", "CC-OPS", "operations", "Marshaller (trainee)", 2400, 500],
    ];
    for (const [department, costCentre, trade, title, basic, housing] of low) {
      this.seqNo++;
      this.employees.push({
        id: `hr-emp-${this.seqNo}`,
        siteId: null,
        employeeNo: `E${this.seqNo}`,
        name: saudiName(),
        nationality: "SA",
        department,
        trade,
        jobTitle: title,
        grade: "A1",
        employment: "direct",
        contractType: "fixed",
        hiredOn: iso(new Date(TODAY.getTime() - 200 * DAY)),
        contractEnd: iso(new Date(TODAY.getTime() + 165 * DAY)),
        leftOn: null,
        status: "active",
        basic,
        housing,
        transport: 300,
        other: 0,
        outsourcedMonthly: null,
        medicalAnnual: null,
        gosiNewEntrant: true,
        weeklyHours: 48,
        costCentre,
        principalId: null,
        principalName: null,
        notes: null,
      });
    }
    for (const o of OUTSOURCED) {
      for (let i = 0; i < o.n; i++) {
        this.seqNo++;
        const nat = pick(o.nats);
        this.employees.push({
          id: `hr-emp-${this.seqNo}`,
          siteId: null,
          employeeNo: `C${this.seqNo}`,
          name: expatName(nat),
          nationality: nat,
          department: o.department,
          trade: o.trade,
          jobTitle: o.title,
          grade: null,
          employment: "outsourced",
          contractType: "fixed",
          hiredOn: hired(),
          contractEnd: iso(new Date(TODAY.getTime() + 300 * DAY)),
          leftOn: null,
          status: "active",
          basic: 0,
          housing: 0,
          transport: 0,
          other: 0,
          // the last of the pavement crew has no charge entered: counted, not costed
          outsourcedMonthly: o.trade === "civil" && i === o.n - 1 ? null : between(o.rate),
          medicalAnnual: null,
          gosiNewEntrant: null,
          weeklyHours: 48,
          costCentre: o.costCentre,
          principalId: null,
          principalName: null,
          notes: o.trade === "civil" && i === o.n - 1 ? "Contract charge not yet received from the contractor" : null,
        });
      }
    }
    // one leaver, kept on the register for end of service and the audit trail
    const leaver = this.employees.find((e) => e.jobTitle === "HVAC technician" && e.nationality !== "SA");
    if (leaver) {
      leaver.status = "left";
      leaver.leftOn = iso(new Date(TODAY.getTime() - 88 * DAY));
      leaver.notes = "Contract ended";
      if (leaver.principalId) {
        this.users = this.users.filter((u) => u.id !== leaver.principalId);
        leaver.principalId = null;
        leaver.principalName = null;
      }
    }
    // overtime on the busiest trades over the last six months
    const months = [...Array(6)].map((_, i) => {
      const d = new Date(Date.UTC(TODAY.getUTCFullYear(), TODAY.getUTCMonth() - i, 1));
      return d.toISOString().slice(0, 7);
    });
    for (const e of this.employees.filter(
      (x) =>
        x.employment === "direct" &&
        ["electrical", "mechanical", "conveyance"].includes(x.trade ?? "") &&
        x.status !== "left",
    )) {
      if (rnd() < 0.45) continue;
      for (const m of months)
        if (rnd() < 0.7) this.overtime.push({ employeeId: e.id, month: m, hours: 6 + Math.round(rnd() * 26) });
    }
    this.fillFiles();
  }

  private rules() {
    return mergeRules(this.restated);
  }

  private view(): HrEmployee[] {
    const rules = this.rules();
    return this.employees.map((e) => describeEmployee(e, rules, TODAY));
  }

  people(): HrPeople {
    const employees = this.view().sort(
      (a, b) => (a.department ?? "").localeCompare(b.department ?? "") || a.name.localeCompare(b.name),
    );
    return {
      employees,
      users: [...this.users].sort((a, b) => a.displayName.localeCompare(b.displayName)),
      departments: [...new Set(employees.map((e) => e.department).filter((d): d is string => !!d))].sort(),
      trades: [...TRADES],
      rulesBasis: this.rules().basis,
    };
  }

  private check(data: Partial<Stored>, currentId?: string) {
    if (data.trade && !(TRADES as readonly string[]).includes(data.trade))
      throw new Error(`trade must be one of ${TRADES.join(", ")}`);
    if (data.principalId) {
      const clash = this.employees.find((e) => e.principalId === data.principalId && e.id !== currentId);
      if (clash) throw new Error(`that user is already bound to employee ${clash.employeeNo}`);
    }
    if (data.status === "left" && !data.leftOn) throw new Error("a leaver needs the date they left");
  }

  add(input: HrEmployeeInput): { id: string; employeeNo: string } {
    if (this.employees.some((e) => e.employeeNo === input.employeeNo))
      throw new Error("that employee number is already used");
    const e: Stored = {
      ...input,
      nationality: input.nationality.toUpperCase(),
      id: `hr-emp-${++this.seqNo}`,
      principalName: this.users.find((u) => u.id === input.principalId)?.displayName ?? null,
    };
    this.check(e);
    this.employees.push(e);
    return { id: e.id, employeeNo: e.employeeNo };
  }

  edit(id: string, patch: HrEmployeePatch): { changed: string[] } {
    const e = this.employees.find((x) => x.id === id);
    if (!e) throw new Error("employee not found");
    const changed = Object.entries(patch)
      .filter(([k, v]) => (e as unknown as Record<string, unknown>)[k] !== v)
      .map(([k]) => k);
    if (!changed.length) return { changed: [] };
    const next = { ...e, ...patch };
    this.check(next, id);
    if (patch.employeeNo && this.employees.some((x) => x.employeeNo === patch.employeeNo && x.id !== id))
      throw new Error("that employee number is already used");
    Object.assign(e, patch, {
      principalName: next.principalId ? (this.users.find((u) => u.id === next.principalId)?.displayName ?? null) : null,
    });
    if (patch.status && changed.includes("status")) this.tell(e.id, "status_changed", null, null, null, patch.status);
    if (changed.some((k) => k.startsWith("clock"))) {
      e.clockSetAt = new Date().toISOString();
      this.tell(e.id, "clock_changed", null);
    }
    return { changed: changed.sort() };
  }

  rulesView(): HrRulesView {
    return {
      rules: this.rules(),
      defaults: defaultRules(),
      restated: this.restated,
      updatedAt: Object.keys(this.restated).length ? this.rulesAt : null,
      updatedBy: Object.keys(this.restated).length ? "Sample HR officer" : null,
    };
  }

  setRules(patch: HrRulesPatch): HrRulesView {
    this.restated = patch;
    this.rulesAt = new Date().toISOString();
    return this.rulesView();
  }

  cost(): HrCost {
    return costSummary(this.view(), this.overtime, this.rules(), TODAY);
  }

  setOvertime(employeeId: string, month: string, hours: number): void {
    if (!this.employees.some((e) => e.id === employeeId)) throw new Error("employee not found");
    this.overtime = this.overtime.filter((o) => !(o.employeeId === employeeId && o.month === month));
    if (hours > 0) this.overtime.push({ employeeId, month, hours });
  }

  /** The sample register's work, with the doer where one of the bound technicians did it. */
  private work(): HrWorkRecord[] {
    const since = new Date(TODAY.getTime() - 365 * DAY).toISOString();
    const byTrade = new Map<string, Stored[]>();
    for (const e of this.employees)
      if (e.principalId && e.trade && e.status !== "left") byTrade.set(e.trade, [...(byTrade.get(e.trade) ?? []), e]);
    const rnd = seq(4242);
    const out: HrWorkRecord[] = [];
    for (const { wo, asset } of this.am.workRecords()) {
      const when = wo.completedAt ?? wo.openedAt;
      const roll = rnd();
      const roll2 = rnd();
      if (when < since || wo.status === "cancelled") continue;
      const elapsed =
        wo.completedAt != null
          ? (new Date(wo.completedAt).getTime() - new Date(wo.openedAt).getTime()) / 3_600_000
          : null;
      // about one job in ten was closed without hours, as on the platform
      const hours = elapsed == null || roll < 0.1 ? null : Math.max(1, Math.round(elapsed * 0.6 * 2) / 2);
      const trade = tradeOfCategory(asset.category, this.rules());
      const crew = trade ? (byTrade.get(trade) ?? []) : [];
      const doer = crew.length && roll2 < 0.65 ? crew[Math.floor(roll2 * 1000) % crew.length] : null;
      out.push({
        id: wo.id,
        number: wo.number,
        title: wo.title,
        kind: wo.kind,
        status: wo.status,
        openedAt: wo.openedAt,
        completedAt: wo.completedAt,
        hours,
        personId: doer?.principalId ?? null,
        assetId: asset.id,
        assetTag: asset.tag,
        assetType: asset.assetType,
        assetTypeName: asset.assetTypeName,
        category: asset.category,
      });
    }
    return out;
  }

  labour(): HrLabour {
    const rules = this.rules();
    const lines = this.view()
      .filter((e) => e.status !== "left")
      .map((e) => costLine(e, rules, TODAY));
    const plans: HrPlanRecord[] = this.am.planRecords().map(({ plan, assetId, category }) => ({
      id: plan.id,
      title: plan.title,
      assetId,
      assetTag: plan.tag ?? null,
      category,
      estimatedHours: plan.estimatedHours ?? null,
      intervalDays: plan.intervalDays,
      active: plan.active,
    }));
    return { ...labour(this.work(), plans, lines, TODAY, 12, rules), rulesBasis: rules.basis };
  }

  saudization(): HrSaudization {
    return saudization(this.view(), this.rules(), TODAY);
  }

  /* ---- the staff files -------------------------------------------------------- */

  private fillFiles() {
    const rnd = seq(20261001);
    const pick = <T>(xs: T[]) => xs[Math.floor(rnd() * xs.length)];
    const women = new Set(["Noura", "Reem", "Sara", "Hessa", "Lama", "Amal"]);
    const districts = ["Al-Aziziyah", "Quba", "Al-Khalidiyyah", "Al-Iskan", "Shouran", "Al-Anbariyah", "Al-Rawabi"];
    const relations = ["Brother", "Wife", "Father", "Mother", "Sister", "Husband"];
    const days = (n: number) => iso(new Date(TODAY.getTime() + n * DAY));
    const digits = (n: number) => Array.from({ length: n }, () => Math.floor(rnd() * 10)).join("");
    for (const e of this.employees) {
      const first = e.name.split(" ")[0];
      const saudi = e.nationality === "SA";
      const age = 23 + Math.floor(rnd() * 34);
      e.dateOfBirth = iso(new Date(TODAY.getTime() - (age * 365.25 + Math.floor(rnd() * 365)) * DAY));
      e.gender = women.has(first) ? "female" : "male";
      e.maritalStatus = pick(["single", "married", "married", "married"]);
      e.idNumber = (saudi ? "1" : "2") + digits(9);
      e.idExpiry = saudi ? days(300 + Math.floor(rnd() * 2500)) : days(-8 + Math.floor(rnd() * 420));
      e.passportNo = saudi ? null : `${e.nationality}${digits(7)}`;
      e.passportExpiry = saudi ? null : days(40 + Math.floor(rnd() * 1800));
      e.phone = `+9665${digits(8)}`;
      e.email =
        e.employment === "direct" ? `${e.name.toLowerCase().replace(/[^a-z]+/g, ".")}@sample-airport.example` : null;
      e.address = `${pick(districts)}, Madinah`;
      e.emergencyContact = {
        name: `${pick(relations)} of ${first}`,
        relation: pick(relations),
        phone: `+9665${digits(8)}`,
      };
    }
    // a few birthdays and anniversaries this month, so "coming up" has something to say
    this.employees.slice(3, 8).forEach((e, i) => {
      const dob = e.dateOfBirth as string;
      e.dateOfBirth = `${dob.slice(0, 4)}-${days(2 + i * 5).slice(5)}`;
    });

    const rec = (e: Stored, r: Partial<HrRecord> & Pick<HrRecord, "kind" | "title">): HrRecord => {
      const out: HrRecord = {
        id: `hr-rec-${++this.recSeq}`,
        employeeId: e.id,
        employeeName: e.name,
        employeeNo: e.employeeNo,
        department: e.department,
        status: "recorded",
        occurredOn: r.startsOn ?? iso(TODAY),
        startsOn: null,
        endsOn: null,
        reference: null,
        data: {},
        confidential: false,
        createdAt: new Date(TODAY.getTime() - 3 * DAY).toISOString(),
        createdByName: "Sample HR officer",
        decidedAt: null,
        decidedByName: null,
        decisionNote: null,
        ...r,
      };
      this.records.push(out);
      return out;
    };
    const direct = this.employees.filter((e) => e.employment === "direct");
    for (const e of direct) {
      const hired = new Date(`${e.hiredOn}T00:00:00Z`);
      const fullYears = Math.floor((TODAY.getTime() - hired.getTime()) / (365.25 * DAY));
      // a year's leave for each year served, a few days short of the entitlement
      for (let y = 1; y <= fullYears; y++) {
        const start = new Date(hired.getTime() + (y * 365.25 - 120 + Math.floor(rnd() * 90)) * DAY);
        if (start >= TODAY) continue;
        const n = (y > 5 ? 30 : 21) - Math.floor(rnd() * 7);
        rec(e, {
          kind: "leave",
          title: "Annual leave",
          status: "approved",
          startsOn: iso(start),
          endsOn: iso(new Date(start.getTime() + (n - 1) * DAY)),
          data: { type: "annual", days: n },
          decidedByName: "Sample HR manager",
          decidedAt: start.toISOString(),
        });
      }
      if (rnd() < 0.4) {
        const k = 1 + Math.floor(rnd() * 3);
        for (let i = 0; i < k; i++) {
          const start = new Date(TODAY.getTime() - Math.floor(10 + rnd() * 330) * DAY);
          const n = 1 + Math.floor(rnd() * 4);
          rec(e, {
            kind: "leave",
            title: "Sick leave",
            status: "approved",
            startsOn: iso(start),
            endsOn: iso(new Date(start.getTime() + (n - 1) * DAY)),
            data: { type: "sick", days: n, certificate: "Medical report attached" },
            decidedByName: "Sample HR officer",
          });
        }
      }
      if (fullYears >= 1) {
        const ratings = [
          "Outstanding",
          "Exceeds expectations",
          "Meets expectations",
          "Meets expectations",
          "Needs improvement",
        ];
        for (let y = 1; y <= Math.min(2, fullYears); y++) {
          const rating = pick(ratings);
          rec(e, {
            kind: "review",
            title: `Annual performance review ${TODAY.getUTCFullYear() - y}`,
            startsOn: `${TODAY.getUTCFullYear() - y + 1}-01-${String(10 + y).padStart(2, "0")}`,
            occurredOn: `${TODAY.getUTCFullYear() - y + 1}-01-${String(10 + y).padStart(2, "0")}`,
            confidential: true,
            data: {
              rating,
              score: { Outstanding: 5, "Exceeds expectations": 4, "Meets expectations": 3, "Needs improvement": 2 }[
                rating
              ],
              strengths: pick([
                "Reliable on shift",
                "Strong on safety",
                "Good with the contractors",
                "Quick fault finding",
              ]),
              goals: pick([
                "Complete the AGL maintenance course",
                "Lead a shift",
                "Reduce repeat call-outs",
                "Mentor a new technician",
              ]),
              reviewer: "Line manager",
            },
          });
        }
      }
      const docs = [e.nationality === "SA" ? "National ID copy" : "Iqama copy", "Employment contract"];
      if (rnd() < 0.5) docs.push("Degree certificate");
      if (e.nationality !== "SA") docs.push("Passport copy");
      for (const title of docs)
        rec(e, {
          kind: "document",
          title,
          occurredOn: e.hiredOn,
          data: { file: `${title.toLowerCase().replace(/ /g, "-")}.pdf` },
        });
    }
    // one long illness, into the three-quarter-pay tier
    const ill = direct[9];
    if (ill)
      rec(ill, {
        kind: "leave",
        title: "Sick leave (surgery)",
        status: "approved",
        startsOn: days(-80),
        endsOn: days(-36),
        data: { type: "sick", days: 45, certificate: "Hospital report" },
      });
    // requests waiting for a decision
    const waiting: [number, string, string, number, number][] = [
      [2, "annual", "Annual leave", 12, 21],
      [14, "annual", "Annual leave", 20, 10],
      [27, "hajj", "Hajj leave", 30, 12],
      [33, "marriage", "Marriage leave", 8, 5],
    ];
    for (const [i, type, title, inDays, n] of waiting) {
      const e = direct[i % direct.length];
      rec(e, {
        kind: "leave",
        title,
        status: "requested",
        startsOn: days(inDays),
        endsOn: days(inDays + n - 1),
        occurredOn: days(-1),
        data: { type, days: n },
      });
    }
    // approved leave now and ahead
    for (const [i, from, n] of [
      [5, -2, 10],
      [19, 3, 14],
      [40, -1, 4],
    ] as [number, number, number][]) {
      const e = direct[i % direct.length];
      rec(e, {
        kind: "leave",
        title: "Annual leave",
        status: "approved",
        startsOn: days(from),
        endsOn: days(from + n - 1),
        occurredOn: days(from - 10),
        data: { type: "annual", days: n },
        decidedByName: "Sample HR manager",
      });
    }
    // discipline
    const disc: [number, string, string, RecordStatus, number][] = [
      [7, "Repeated late arrival", "Verbal warning", "closed", -140],
      [21, "Absent without notice", "Written warning", "open", -20],
      [30, "Safety rule breach on the apron", "Final written warning", "open", -9],
      [44, "Late arrival", "Verbal warning", "closed", -300],
    ];
    for (const [i, title, action, status, when] of disc) {
      const e = direct[i % direct.length];
      rec(e, {
        kind: "discipline",
        title,
        status,
        occurredOn: days(when),
        confidential: true,
        data: { action, article: "Labour Law art. 66; the airport's work regulations" },
      });
    }
    // letters
    const letters: [number, string, string, RecordStatus][] = [
      [1, "employment", "Employment verification letter", "issued"],
      [4, "salary", "Salary certificate", "issued"],
      [11, "bank", "Salary transfer letter to a bank", "issued"],
      [16, "embassy", "Letter to an embassy (visa)", "issued"],
      [22, "employment", "Employment verification letter", "requested"],
      [26, "salary", "Salary certificate", "requested"],
      [35, "noc", "No-objection letter (NOC)", "requested"],
    ];
    for (const [i, type, title, status] of letters) {
      const e = direct[i % direct.length];
      rec(e, {
        kind: "letter",
        title,
        status,
        occurredOn: days(status === "issued" ? -Math.floor(5 + rnd() * 60) : -1),
        reference:
          status === "issued" ? `HR-L-${TODAY.getUTCFullYear()}-${String(++this.letterSeq).padStart(4, "0")}` : null,
        data: { letter_type: type, addressee: type === "bank" ? "Al Rajhi Bank" : undefined },
      });
    }
    rec(direct[3], { kind: "note", title: "Asked to move to the night shift from next month", occurredOn: days(-6) });

    // attendance: the last sixty days, Friday off, leave as recorded
    const onLeave = new Map<string, string>();
    for (const r of this.records)
      if (r.kind === "leave" && r.status === "approved" && r.startsOn) {
        const st = r.data.type === "sick" ? "sick" : "leave";
        for (
          let t = new Date(`${r.startsOn}T00:00:00Z`).getTime();
          t <= new Date(`${r.endsOn ?? r.startsOn}T00:00:00Z`).getTime();
          t += DAY
        )
          onLeave.set(`${r.employeeId}|${iso(new Date(t))}`, st);
      }
    const today = iso(TODAY);
    for (let back = 60; back >= 0; back--) {
      const dayD = new Date(TODAY.getTime() - back * DAY);
      const day = iso(dayD);
      const isHoliday = day.slice(5) === "09-23" || day.slice(5) === "02-22";
      for (const [idx, e] of this.employees.entries()) {
        if (e.status === "left" || e.hiredOn > day) continue;
        // today: about two thirds already recorded, the rest to take
        if (day === today && idx % 3 === 0) continue;
        let status: AttendanceStatus;
        let checkIn: string | null = null;
        let late: number | null = null;
        const leave = onLeave.get(`${e.id}|${day}`);
        if (dayD.getUTCDay() === 5) status = "off";
        else if (isHoliday) status = "holiday";
        else if (leave) status = leave as AttendanceStatus;
        else {
          const x = rnd();
          if (x < 0.025) status = "absent";
          else if (x < 0.09) {
            status = "late";
            late = 10 + Math.floor(rnd() * 60);
            checkIn = `0${7 + Math.floor(late / 60)}:${String(late % 60).padStart(2, "0")}`;
          } else if (x < 0.11 && e.department === "Administration") status = "remote";
          else {
            status = "present";
            checkIn = `06:${String(40 + Math.floor(rnd() * 20)).padStart(2, "0")}`;
          }
        }
        const worked = status === "present" || status === "late";
        this.attendance.set(`${e.id}|${day}`, {
          employeeId: e.id,
          day,
          status,
          checkIn,
          checkOut: worked && day !== today ? `15:${String(Math.floor(rnd() * 30)).padStart(2, "0")}` : null,
          minutesLate: late,
          note: null,
        });
      }
    }
    // HR levels in the sample: the operator is the super admin; admin staff hold the rest
    const admins = this.employees.filter((e) => e.department === "Administration" || e.department === "Management");
    const levels = ["hr_manager", "payroll_officer", "hr_officer", "hr_assistant", "hr_assistant"];
    admins.slice(0, levels.length).forEach((e, i) => {
      if (!e.principalId) {
        e.principalId = `hr-user-${e.id}`;
        e.principalName = e.name;
        this.users.push({ id: e.principalId, displayName: e.name, email: e.email ?? null });
      }
      this.access.set(e.principalId, levels[i]);
    });
    this.users.push({ id: "operator", displayName: "Operator", email: null });
    this.access.set("operator", "hr_super_admin");
    this.fillClock();
  }

  staffFile(id: string): HrStaffFile {
    const e = this.view().find((x) => x.id === id);
    if (!e) throw new Error("employee not found");
    const rules = this.rules();
    return {
      employee: { ...e, hasPhoto: this.photos.has(id) },
      photo: this.photos.get(id) ?? null,
      records: this.records.filter((r) => r.employeeId === id).sort((a, b) => b.occurredOn.localeCompare(a.occurredOn)),
      attendance: [...this.attendance.values()]
        .filter((a) => a.employeeId === id)
        .sort((a, b) => b.day.localeCompare(a.day)),
      cost: costLine(e, rules, TODAY),
      can: { pay: true, confidential: true, write: true, approve: true },
      confidentialHidden: false,
    };
  }

  setPhoto(id: string, dataUrl: string) {
    if (!/^data:image\/(jpeg|png|webp);base64,/.test(dataUrl)) throw new Error("a photo is a JPEG, PNG or WebP image");
    this.photos.set(id, dataUrl);
  }

  listRecords(filter: { kind?: string; status?: string } = {}): { records: HrRecord[]; confidentialHidden: boolean } {
    return {
      records: this.records
        .filter((r) => (!filter.kind || r.kind === filter.kind) && (!filter.status || r.status === filter.status))
        .sort((a, b) => b.occurredOn.localeCompare(a.occurredOn)),
      confidentialHidden: false,
    };
  }

  private markLeave(r: HrRecord) {
    if (r.kind !== "leave" || !r.startsOn) return;
    const st: AttendanceStatus = r.data.type === "sick" ? "sick" : "leave";
    for (
      let t = new Date(`${r.startsOn}T00:00:00Z`).getTime();
      t <= new Date(`${r.endsOn ?? r.startsOn}T00:00:00Z`).getTime();
      t += DAY
    ) {
      const day = iso(new Date(t));
      this.attendance.set(`${r.employeeId}|${day}`, {
        employeeId: r.employeeId,
        day,
        status: st,
        checkIn: null,
        checkOut: null,
        minutesLate: null,
        note: null,
      });
    }
  }

  addRecord(input: HrRecordInput): HrRecord {
    const e = this.employees.find((x) => x.id === input.employeeId);
    if (!e) throw new Error("employee not found");
    const status: RecordStatus =
      input.status ??
      ({ leave: "requested", letter: "requested", discipline: "open" } as Record<string, RecordStatus>)[input.kind] ??
      "recorded";
    const r: HrRecord = {
      id: `hr-rec-${++this.recSeq}`,
      employeeId: e.id,
      employeeName: e.name,
      employeeNo: e.employeeNo,
      department: e.department,
      kind: input.kind,
      status,
      title: input.title,
      occurredOn: input.occurredOn ?? iso(new Date()),
      startsOn: input.startsOn ?? null,
      endsOn: input.endsOn ?? null,
      reference:
        input.kind === "letter" && status === "issued"
          ? `HR-L-${new Date().getUTCFullYear()}-${String(++this.letterSeq).padStart(4, "0")}`
          : null,
      data: input.data ?? {},
      confidential: input.kind === "review" || input.kind === "discipline",
      createdAt: new Date().toISOString(),
      createdByName: "Operator",
      decidedAt: status === "approved" || status === "issued" ? new Date().toISOString() : null,
      decidedByName: status === "approved" || status === "issued" ? "Operator" : null,
      decisionNote: null,
    };
    this.records.push(r);
    if (status === "approved") this.markLeave(r);
    if (r.confidential) this.tell(e.id, "confidential", r.id);
    else if (["leave", "letter", "request"].includes(r.kind))
      this.tell(
        e.id,
        ({ requested: "received", approved: "approved", issued: "issued" } as Record<string, NotifyEvent>)[status] ??
          null,
        r.id,
        r.title,
        r.reference,
      );
    return r;
  }

  decide(id: string, status: RecordStatus, note?: string | null): HrRecord {
    const r = this.records.find((x) => x.id === id);
    if (!r) throw new Error("record not found");
    if (["rejected", "cancelled", "closed"].includes(r.status)) throw new Error(`this record is already ${r.status}`);
    if (status === "issued" && r.kind !== "letter") throw new Error("only a letter is issued");
    r.status = status;
    r.decisionNote = note ?? null;
    r.decidedAt = new Date().toISOString();
    r.decidedByName = "Operator";
    if (status === "issued" && !r.reference)
      r.reference = `HR-L-${new Date().getUTCFullYear()}-${String(++this.letterSeq).padStart(4, "0")}`;
    if (status === "approved") this.markLeave(r);
    if (["leave", "letter", "request"].includes(r.kind))
      this.tell(r.employeeId, status as NotifyEvent, r.id, r.title, r.reference, null, note ?? null);
    return r;
  }

  attendanceDay(day: string): HrAttendanceDay {
    return {
      day,
      rows: this.employees
        .filter((e) => e.status !== "left" && e.hiredOn <= day)
        .sort((a, b) => (a.department ?? "").localeCompare(b.department ?? "") || a.name.localeCompare(b.name))
        .map((e) => {
          const a = this.attendance.get(`${e.id}|${day}`);
          return {
            employeeId: e.id,
            employeeNo: e.employeeNo,
            name: e.name,
            department: e.department,
            employment: e.employment,
            status: a?.status ?? null,
            checkIn: a?.checkIn ?? null,
            checkOut: a?.checkOut ?? null,
            minutesLate: a?.minutesLate ?? null,
            note: a?.note ?? null,
            source: a ? "manual" : null,
          };
        }),
    };
  }

  attendanceRange(from: string, to: string, employeeId?: string): HrAttendanceRow[] {
    return [...this.attendance.values()]
      .filter((a) => a.day >= from && a.day <= to && (!employeeId || a.employeeId === employeeId))
      .sort((a, b) => a.day.localeCompare(b.day));
  }

  setAttendance(input: HrAttendanceInput): { recorded: number } {
    for (const id of input.employeeIds)
      this.attendance.set(`${id}|${input.day}`, {
        employeeId: id,
        day: input.day,
        status: input.status,
        checkIn: input.checkIn ?? null,
        checkOut: input.checkOut ?? null,
        minutesLate: input.minutesLate ?? null,
        note: input.note ?? null,
      });
    return { recorded: input.employeeIds.length };
  }

  accessView(): HrAccessView {
    const ROLES: [string, string, string, string[]][] = [
      [
        "hr_super_admin",
        "HR Super Admin",
        "Everything in Human Resources, including the rules and who has which HR level",
        ["hr:read", "hr:write", "hr:pay", "hr:confidential", "hr:approve", "hr:admin"],
      ],
      [
        "hr_manager",
        "HR Manager",
        "Every file, pay, reviews and discipline; approves requests",
        ["hr:read", "hr:write", "hr:pay", "hr:confidential", "hr:approve"],
      ],
      [
        "payroll_officer",
        "Payroll Officer",
        "Pay, overtime, manpower cost and end of service; no reviews or discipline",
        ["hr:read", "hr:write", "hr:pay"],
      ],
      [
        "hr_officer",
        "HR Officer",
        "Keeps the staff files, attendance and leave; approves requests; no pay",
        ["hr:read", "hr:write", "hr:approve"],
      ],
      [
        "hr_assistant",
        "HR Assistant",
        "Admin staff: personal details, attendance, leave and letter requests; no pay",
        ["hr:read", "hr:write"],
      ],
    ];
    const byId = new Map(this.users.map((u) => [u.id, u]));
    return {
      roles: ROLES.map(([key, name, description, permissions]) => ({
        key,
        name,
        description,
        permissions,
        holders: [...this.access.entries()]
          .filter(([, r]) => r === key)
          .map(([id]) => ({ id, displayName: byId.get(id)?.displayName ?? id, email: byId.get(id)?.email ?? null })),
      })),
      users: [...this.users].sort((a, b) => a.displayName.localeCompare(b.displayName)),
      canAssign: true,
    };
  }

  setAccess(principalId: string, role: string | null) {
    if (principalId === "operator" && role !== "hr_super_admin")
      throw new Error("you cannot lower your own HR level; ask another super admin");
    if (role) this.access.set(principalId, role);
    else this.access.delete(principalId);
  }

  /* ---- check-in, requests from the phone, and the outbox ------------------------ */

  private fillClock() {
    const rnd = seq(20261002);
    // Prince Mohammad bin Abdulaziz International Airport, Madinah
    this.fences = [
      {
        id: "gf-terminal",
        name: "Passenger terminal",
        lat: 24.5534,
        lon: 39.7051,
        radiusM: 250,
        shiftStart: "07:00",
        graceMin: 15,
        active: true,
      },
      {
        id: "gf-afm",
        name: "Airfield maintenance base",
        lat: 24.5452,
        lon: 39.6966,
        radiusM: 150,
        shiftStart: "06:30",
        graceMin: 10,
        active: true,
      },
      {
        id: "gf-admin",
        name: "Administration building",
        lat: 24.5561,
        lon: 39.7089,
        radiusM: 120,
        shiftStart: "08:00",
        graceMin: 15,
        active: true,
      },
    ];
    // how each person checks in: HR's decision, by area and job
    for (const e of this.employees) {
      if (e.status === "left") continue;
      const r = rnd();
      if (r < 0.08) continue; // a few not decided yet
      if (e.employment === "outsourced") {
        e.clockMethods = ["terminal_fingerprint"];
        e.clockBasis = "Contractor staff; fingerprint reader at the contractor gate";
      } else if (e.department === "Airfield Maintenance" || e.department === "Airside Operations") {
        e.clockMethods = e.jobTitle?.includes("engineer")
          ? ["phone_location", "terminal_fingerprint"]
          : ["terminal_fingerprint"];
        e.clockBasis = e.jobTitle?.includes("engineer")
          ? "Field staff across the airfield; checks in by phone at the location"
          : "Works airside; restricted area — fingerprint reader only";
        e.clockGeofenceIds = e.clockMethods.includes("phone_location") ? ["gf-afm"] : null;
      } else if (e.department === "Administration" || e.department === "Management") {
        e.clockMethods = ["phone_location", "terminal_face"];
        e.clockBasis = "Office-based; the building has a face reader";
      } else {
        e.clockMethods = ["terminal_face", "terminal_fingerprint"];
        e.clockBasis = "Shift work in the terminal; face reader at the staff entrance";
      }
      e.clockSetAt = new Date(TODAY.getTime() - 30 * DAY).toISOString();
    }
    const fenceOf = (e: Stored) =>
      e.department === "Airfield Maintenance"
        ? this.fences[1]
        : e.department === "Administration" || e.department === "Management"
          ? this.fences[2]
          : this.fences[0];
    for (const a of this.attendance.values()) {
      if (a.day < iso(new Date(TODAY.getTime() - 7 * DAY))) continue;
      if (!(a.status === "present" || a.status === "late") || !a.employeeId) continue;
      const e = this.employees.find((x) => x.id === a.employeeId);
      if (!e) continue;
      const f = fenceOf(e);
      const allowed: ClockMethod[] = e.clockMethods?.length
        ? e.clockMethods
        : ["terminal_face", "terminal_fingerprint"];
      const method: ClockMethod = allowed[Math.floor(rnd() * allowed.length)];
      const phone = method === "phone_location";
      const jitter = () => (rnd() - 0.5) * (f.radiusM / 111_000);
      const at = (t: string | null) => `${a.day}T${t ?? "07:00"}:00+03:00`;
      const mk = (direction: "in" | "out", t: string | null): HrClockEvent => {
        const lat = phone ? f.lat + jitter() : null;
        const lon = phone ? f.lon + jitter() : null;
        return {
          id: `ce-${this.clockEvents.length + 1}`,
          employeeId: e.id,
          name: e.name,
          employeeNo: e.employeeNo,
          department: e.department,
          direction,
          at: at(t),
          method,
          lat,
          lon,
          accuracyM: phone ? 5 + Math.round(rnd() * 25) : null,
          distanceM: phone && lat != null && lon != null ? Math.round(locate([f], lat, lon, 0).distanceM ?? 0) : null,
          inside: true,
          verified: !phone,
          device: phone
            ? "Employee's phone"
            : method === "terminal_face"
              ? "Face reader, main gate"
              : "Fingerprint reader, staff entrance",
          note: null,
          place: f.name,
        };
      };
      this.clockEvents.push(mk("in", a.checkIn));
      if (a.checkOut) this.clockEvents.push(mk("out", a.checkOut));
    }
    // the outbox for what is already on the files
    for (const r of this.records) {
      if (!["leave", "letter", "request"].includes(r.kind) || r.occurredOn < iso(new Date(TODAY.getTime() - 60 * DAY)))
        continue;
      this.tell(r.employeeId, "received", r.id, r.title, null, null, null, r.createdAt);
      if (r.status === "approved" || r.status === "issued")
        this.tell(r.employeeId, r.status, r.id, r.title, r.reference, null, null, r.decidedAt ?? r.createdAt);
    }
    // the operator's own record, for "My check-in"
    const me = this.employees.find((e) => e.department === "Management");
    if (me) {
      me.principalId = "operator";
      me.principalName = "Operator";
      this.meId = me.id;
    }
  }

  private tell(
    employeeId: string,
    event: NotifyEvent | null,
    recordId: string | null,
    title?: string | null,
    reference?: string | null,
    status?: string | null,
    note?: string | null,
    at?: string,
  ) {
    if (!event) return;
    const e = this.employees.find((x) => x.id === employeeId);
    if (!e) return;
    const words: Record<NotifyEvent, [string, string]> = {
      received: ["استلمنا طلبك", "We have received your request"],
      approved: ["تمت الموافقة على طلبك", "Your request has been approved"],
      rejected: ["لم تتم الموافقة على طلبك", "Your request has not been approved"],
      issued: ["صدر خطابك", "Your letter has been issued"],
      closed: ["أُغلق الطلب", "Your request has been closed"],
      cancelled: ["أُلغي الطلب", "Your request has been cancelled"],
      status_changed: ["تغيّرت حالتك الوظيفية", "Your employment status has changed"],
      confidential: ["أُضيف سجل إلى ملفك", "A record has been added to your file"],
      clock_changed: ["تغيّرت طريقة تسجيل حضورك", "How you check in has changed"],
    };
    const [ar, en] = words[event];
    const what = event === "confidential" ? "" : event === "status_changed" ? `: ${status}` : title ? `: ${title}` : "";
    const ref = reference ? ` (${reference})` : "";
    const sms = `Sample estate: ${ar}${what}${ref}\n${en}${what}${ref}${note ? `\n${note}` : ""}`;
    for (const channel of ["sms", "email"] as const) {
      const to = channel === "sms" ? (e.phone ?? null) : (e.email ?? null);
      this.outbox.push({
        id: `nt-${++this.notifySeq}`,
        employeeId: e.id,
        name: e.name,
        employeeNo: e.employeeNo,
        recordId,
        channel,
        toAddress: to,
        subject: channel === "email" ? `${en}${what}${ref} | ${ar}` : null,
        body:
          channel === "sms"
            ? sms
            : `عزيزي/عزيزتي ${e.name.split(" ")[0]}،\n\n${ar}${what}${ref}\n\n— — —\n\nDear ${e.name.split(" ")[0]},\n\n${en}${what}${ref}${note ? `\n${note}` : ""}\n\nHuman Resources`,
        event,
        status: to ? "not_configured" : "no_address",
        attempts: 0,
        error: to
          ? "the demonstration sends nothing"
          : channel === "sms"
            ? "no mobile number on file"
            : "no email on file",
        createdAt: at ?? new Date().toISOString(),
        sentAt: null,
      });
    }
  }

  geofences(): HrGeofence[] {
    return this.fences.map((f) => ({ ...f }));
  }

  addGeofence(f: Omit<HrGeofence, "id">): HrGeofence {
    const g = { ...f, id: `gf-${this.fences.length + 1}-${Date.now()}`, active: true };
    this.fences.push(g);
    return g;
  }

  editGeofence(id: string, patch: Partial<HrGeofence>) {
    const g = this.fences.find((x) => x.id === id);
    if (!g) throw new Error("location not found");
    Object.assign(g, patch);
  }

  clockDay(day: string, employeeId?: string): HrClockEvent[] {
    return this.clockEvents
      .filter((c) => c.at.slice(0, 10) === day && (!employeeId || c.employeeId === employeeId))
      .sort((a, b) => b.at.localeCompare(a.at));
  }

  private applyDay(employeeId: string, direction: "in" | "out", at: Date, fence: HrGeofence | null) {
    const local = new Date(at.getTime() + 3 * 3_600_000);
    const day = local.toISOString().slice(0, 10);
    const time = local.toISOString().slice(11, 16);
    const key = `${employeeId}|${day}`;
    const row = this.attendance.get(key);
    if (row && ["leave", "sick", "holiday"].includes(row.status)) return;
    if (direction === "in") {
      if (row?.checkIn && row.checkIn <= time) return;
      const s = dayStatus(time, fence?.shiftStart ?? "07:00", fence?.graceMin ?? 15);
      this.attendance.set(key, {
        employeeId,
        day,
        status: s.status,
        checkIn: time,
        checkOut: row?.checkOut ?? null,
        minutesLate: s.minutesLate || null,
        note: null,
      });
    } else {
      this.attendance.set(key, {
        employeeId,
        day,
        status: row?.status ?? "present",
        checkIn: row?.checkIn ?? null,
        checkOut: time,
        minutesLate: row?.minutesLate ?? null,
        note: null,
      });
    }
  }

  clockManual(employeeId: string, direction: "in" | "out", at: string, note: string) {
    const e = this.employees.find((x) => x.id === employeeId);
    if (!e) throw new Error("employee not found");
    if (note.trim().length < 3) throw new Error("say why it is recorded by hand");
    this.clockEvents.push({
      id: `ce-${this.clockEvents.length + 1}`,
      employeeId,
      name: e.name,
      employeeNo: e.employeeNo,
      department: e.department,
      direction,
      at: `${at.length === 16 ? at : at.slice(0, 16)}:00+03:00`,
      method: "manual",
      lat: null,
      lon: null,
      accuracyM: null,
      distanceM: null,
      inside: null,
      verified: false,
      device: null,
      note,
      place: null,
    });
    this.applyDay(employeeId, direction, new Date(`${at.slice(0, 16)}:00+03:00`), null);
  }

  me(): HrMe {
    const e = this.employees.find((x) => x.id === this.meId);
    if (!e) throw new Error("your sign-in is not linked to an employee record; ask HR to link it");
    const today = iso(new Date());
    const a = this.attendance.get(`${e.id}|${today}`);
    return {
      employee: { id: e.id, employeeNo: e.employeeNo, name: e.name, department: e.department, jobTitle: e.jobTitle },
      hasPhone: !!e.phone,
      hasEmail: !!e.email,
      clockMethods: e.clockMethods ?? null,
      clockBasis: e.clockBasis ?? null,
      phoneAllowed: !!e.clockMethods?.includes("phone_location"),
      today: a ? { status: a.status, checkIn: a.checkIn, checkOut: a.checkOut, minutesLate: a.minutesLate } : null,
      events: this.clockDay(today, e.id)
        .slice()
        .reverse()
        .map((c) => ({
          direction: c.direction,
          at: c.at,
          method: c.method,
          inside: c.inside,
          distanceM: c.distanceM,
          place: c.place,
        })),
      requests: this.records
        .filter((r) => r.employeeId === e.id && ["leave", "letter", "request"].includes(r.kind))
        .sort((x, y) => y.createdAt.localeCompare(x.createdAt))
        .slice(0, 30)
        .map((r) => ({
          id: r.id,
          kind: r.kind,
          status: r.status,
          title: r.title,
          occurredOn: r.occurredOn,
          startsOn: r.startsOn,
          endsOn: r.endsOn,
          reference: r.reference,
          decisionNote: r.decisionNote,
          decidedAt: r.decidedAt,
        })),
      locations: this.geofences().filter((f) => !e.clockGeofenceIds || e.clockGeofenceIds.includes(f.id)),
    };
  }

  clockMe(input: HrClockInput) {
    const e = this.employees.find((x) => x.id === this.meId);
    if (!e) throw new Error("your sign-in is not linked to an employee record");
    if (this.clockEvents.some((c) => c.id === input.clientId)) return { duplicate: true };
    if (!e.clockMethods?.length)
      throw new Error("HR has not yet set how you check in; use a face or fingerprint reader, or ask HR");
    if (!e.clockMethods.includes("phone_location"))
      throw new Error(
        `HR has set you to check in at a ${e.clockMethods.map((m) => (m === "terminal_face" ? "face reader" : "fingerprint reader")).join(" or ")}${e.clockBasis ? ` (${e.clockBasis})` : ""}, not by phone`,
      );
    const allowed = this.fences.filter((f) => !e.clockGeofenceIds || e.clockGeofenceIds.includes(f.id));
    const where = locate(allowed, input.lat, input.lon, input.accuracyM);
    if (!where.inside || !where.fence) throw new Error(where.reason ?? "outside every check-in location");
    const now = new Date();
    this.clockEvents.push({
      id: input.clientId,
      employeeId: e.id,
      name: e.name,
      employeeNo: e.employeeNo,
      department: e.department,
      direction: input.direction,
      at: now.toISOString(),
      method: "phone_location",
      lat: input.lat,
      lon: input.lon,
      accuracyM: input.accuracyM,
      distanceM: where.distanceM,
      inside: true,
      verified: false,
      device: input.device ?? null,
      note: null,
      place: where.fence.name,
    });
    this.applyDay(e.id, input.direction, now, where.fence);
    return { at: now.toISOString(), place: where.fence.name, distanceM: where.distanceM, method: "phone_location" };
  }

  selfRequest(input: HrSelfRequest): HrRecord {
    if (!this.meId) throw new Error("your sign-in is not linked to an employee record");
    const def = REQUEST_BY.get(input.type);
    const kind = def?.kind ?? "request";
    if (kind === "leave" && !input.startsOn) throw new Error("a leave request needs its first day");
    const end = input.endsOn || input.startsOn;
    const days =
      kind === "leave" && input.startsOn && end
        ? Math.round((Date.parse(end) - Date.parse(input.startsOn)) / DAY) + 1
        : undefined;
    return this.addRecord({
      employeeId: this.meId,
      kind,
      title: input.title,
      startsOn: input.startsOn ?? null,
      endsOn: end ?? null,
      data:
        kind === "letter"
          ? { letter_type: input.letterType ?? "employment", details: input.details, self_service: true }
          : { type: input.type, days, details: input.details, self_service: true },
    });
  }

  notifications(filter: { employeeId?: string; status?: string } = {}): {
    notifications: HrNotification[];
    channels: HrChannels;
  } {
    return {
      notifications: this.outbox
        .filter(
          (n) =>
            (!filter.employeeId || n.employeeId === filter.employeeId) &&
            (!filter.status || n.status === filter.status),
        )
        .sort((a, b) => b.createdAt.localeCompare(a.createdAt)),
      channels: this.channels(),
    };
  }

  channels(): HrChannels {
    return {
      email: { configured: false, provider: null, from: null },
      sms: { configured: false, provider: null, sender: null },
    };
  }

  sendNow() {
    const due = this.outbox.filter((n) => n.status === "queued");
    for (const n of due) {
      n.status = "not_configured";
      n.error = "the demonstration sends nothing";
    }
    return { sent: 0, failed: 0, not_configured: due.length };
  }

  retry(id: string) {
    const n = this.outbox.find((x) => x.id === id);
    if (!n) throw new Error("message not found");
    if (!n.toAddress) throw new Error("there is no number or email on file to send it to");
    n.status = "queued";
    n.error = null;
  }

  /** The spreadsheet import, as the platform does it: checked, then saved, silently. */
  importPeople(
    rows: { row: number; data: Record<string, unknown>; loginEmail: string | null; leaveBalance: number | null }[],
    opts: { dryRun: boolean; updateExisting: boolean },
  ): ImportResponse {
    const camel = (k: string) => k.replace(/_([a-z])/g, (_, c: string) => c.toUpperCase());
    const results: ImportResult[] = [];
    const seen = new Set<string>();
    const rules = this.rules();
    for (const r of rows) {
      const d: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(r.data)) if (v != null && v !== "") d[camel(k)] = v;
      const no = String(d.employeeNo ?? "").trim();
      const base = { row: r.row, employeeNo: no || null };
      if (!no) {
        results.push({ ...base, action: "failed", error: "no employee number" });
        continue;
      }
      if (seen.has(no)) {
        results.push({ ...base, action: "failed", error: "this employee number appears twice in the sheet" });
        continue;
      }
      seen.add(no);
      let warning: string | undefined;
      if (r.loginEmail) {
        const u = this.users.find((x) => (x.email ?? "").toLowerCase() === r.loginEmail?.toLowerCase());
        if (u) d.principalId = u.id;
        else warning = `no platform sign-in with the email ${r.loginEmail}; not linked`;
      }
      const existing = this.employees.find((e) => e.employeeNo === no);
      try {
        let emp: Stored;
        let action: ImportResult["action"];
        let changed: string[] | undefined;
        if (existing) {
          if (!opts.updateExisting) {
            results.push({ ...base, action: "skipped", id: existing.id, error: "already on the register" });
            continue;
          }
          changed = Object.keys(d).filter(
            (k) => JSON.stringify((existing as unknown as Record<string, unknown>)[k] ?? null) !== JSON.stringify(d[k]),
          );
          if (changed.length) this.check({ ...existing, ...d } as Stored, existing.id);
          if (!opts.dryRun && changed.length)
            Object.assign(existing, Object.fromEntries(changed.map((k) => [k, d[k]])));
          emp = existing;
          action = changed.length ? "updated" : "unchanged";
        } else {
          for (const [k, label] of [
            ["name", "name"],
            ["nationality", "nationality"],
            ["hiredOn", "hired_on"],
          ] as const)
            if (!d[k]) throw new Error(`${label}: Field required`);
          if (!/^\d{4}-\d{2}-\d{2}$/.test(String(d.hiredOn))) throw new Error("hired_on: Input should be a valid date");
          const input = {
            siteId: null,
            employeeNo: no,
            name: String(d.name),
            nationality: String(d.nationality).toUpperCase(),
            department: (d.department as string) ?? null,
            trade: (d.trade as Stored["trade"]) ?? null,
            jobTitle: (d.jobTitle as string) ?? null,
            grade: (d.grade as string) ?? null,
            employment: (d.employment as Stored["employment"]) ?? "direct",
            contractType: (d.contractType as Stored["contractType"]) ?? "indefinite",
            hiredOn: String(d.hiredOn),
            contractEnd: (d.contractEnd as string) ?? null,
            leftOn: (d.leftOn as string) ?? null,
            status: (d.status as Stored["status"]) ?? "active",
            basic: Number(d.basic ?? 0),
            housing: Number(d.housing ?? 0),
            transport: Number(d.transport ?? 0),
            other: Number(d.other ?? 0),
            outsourcedMonthly: (d.outsourcedMonthly as number) ?? null,
            medicalAnnual: (d.medicalAnnual as number) ?? null,
            gosiNewEntrant: null,
            weeklyHours: Number(d.weeklyHours ?? 48),
            costCentre: (d.costCentre as string) ?? null,
            principalId: (d.principalId as string) ?? null,
            notes: null,
            dateOfBirth: (d.dateOfBirth as string) ?? null,
            gender: (d.gender as Stored["gender"]) ?? null,
            maritalStatus: (d.maritalStatus as Stored["maritalStatus"]) ?? null,
            idNumber: (d.idNumber as string) ?? null,
            idExpiry: (d.idExpiry as string) ?? null,
            passportNo: (d.passportNo as string) ?? null,
            passportExpiry: (d.passportExpiry as string) ?? null,
            phone: (d.phone as string) ?? null,
            email: (d.email as string) ?? null,
            address: (d.address as string) ?? null,
            emergencyContact: (d.emergencyContact as Stored["emergencyContact"]) ?? null,
            clockMethods: (d.clockMethods as Stored["clockMethods"]) ?? null,
          };
          if (opts.dryRun) {
            this.check(input as unknown as Stored);
            emp = { ...(input as unknown as Stored), id: "dry-run" };
          } else {
            const made = this.add(input);
            emp = this.employees.find((e) => e.id === made.id) as Stored;
          }
          action = "added";
        }
        if (r.leaveBalance != null) {
          if (emp.employment === "outsourced")
            warning = "an outsourced head's leave is the contractor's; balance not kept";
          else if (!opts.dryRun) {
            const years = serviceYearsOf(emp.hiredOn);
            const lv = rules.leave;
            const earned = Math.min(years, lv.afterYears) * lv.days + Math.max(0, years - lv.afterYears) * lv.daysAfter;
            for (const x of this.records)
              if (x.employeeId === emp.id && x.data.opening && x.status !== "cancelled") x.status = "cancelled";
            this.records.push({
              id: `hr-rec-${++this.recSeq}`,
              employeeId: emp.id,
              employeeName: emp.name,
              employeeNo: emp.employeeNo,
              department: emp.department,
              kind: "leave",
              status: "approved",
              title: "Leave taken before Sky Logics (opening balance)",
              occurredOn: iso(new Date()),
              startsOn: null,
              endsOn: null,
              reference: null,
              data: {
                type: "annual",
                days: Math.round((earned - r.leaveBalance) * 100) / 100,
                opening: true,
                balance: r.leaveBalance,
              },
              confidential: false,
              createdAt: new Date().toISOString(),
              createdByName: "Operator",
              decidedAt: new Date().toISOString(),
              decidedByName: "Operator",
              decisionNote: null,
            });
          }
        }
        results.push({ ...base, action, id: emp.id, changed, ...(warning ? { warning } : {}) });
      } catch (e) {
        results.push({ ...base, action: "failed", error: e instanceof Error ? e.message : String(e) });
      }
    }
    const counts = { added: 0, updated: 0, unchanged: 0, skipped: 0, failed: 0 };
    for (const x of results) counts[x.action]++;
    return { dryRun: opts.dryRun, counts, results };
  }
}

function serviceYearsOf(hiredOn: string): number {
  return Math.max(0, (Date.now() - Date.parse(`${hiredOn}T00:00:00Z`)) / (365.25 * DAY));
}
